import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import { format } from "date-fns";
import WorkoutDetail from "./WorkoutDetail";
import EditWorkoutDialog from "./EditWorkoutDialog";
import { useExercisesMap } from "../../plan/hooks/useExercisesMap";

/**
 * Recent completed sessions (reportExercise docs), expandable — mirrors coach "Workout history".
 */
function RecentWorkoutSessionsList({ workouts, organizationId, clientId, weightUnit, aggregated }) {
  const { findById } = useExercisesMap(organizationId, !!organizationId);
  const [expanded, setExpanded] = useState(false);
  const [editorWorkout, setEditorWorkout] = useState(null);

  const sessions = useMemo(() => {
    if (!Array.isArray(workouts)) return [];
    return [...workouts].sort((a, b) => {
      const da = a.completedOn || a.createdOn;
      const db = b.completedOn || b.createdOn;
      const ta = da ? new Date(da).getTime() : 0;
      const tb = db ? new Date(db).getTime() : 0;
      return tb - ta;
    });
  }, [workouts]);

  if (sessions.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No completed workouts in the last 90 days yet.
        </Typography>
      </Box>
    );
  }

  const preview = sessions.slice(0, 12);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <FitnessCenterIcon color="primary" sx={{ fontSize: 28 }} />
        <Box>
          <Typography variant="subtitle1" fontWeight={700} color="primary">
            Recent sessions
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Tap a session to see exercises and sets (same detail your coach sees).
          </Typography>
        </Box>
      </Box>
      {preview.map((w) => {
        const d = w.completedOn || w.createdOn;
        const dateStr = d ? format(new Date(d), "EEEE, MMM d, yyyy") : "—";
        const splitName = w.reportData?.split?.name ?? "";
        const summary = [dateStr, splitName].filter(Boolean).join(" · ");
        return (
          <Accordion
            key={w.id}
            expanded={expanded === w.id}
            onChange={(_, isExp) => setExpanded(isExp ? w.id : false)}
            elevation={0}
            sx={{
              mb: 1,
              border: (t) => `1px solid ${t.palette.divider}`,
              borderRadius: "12px !important",
              "&:before": { display: "none" },
              overflow: "hidden",
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" fontWeight={600}>
                {summary}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, bgcolor: "grey.50" }}>
              <WorkoutDetail
                workout={w}
                findById={findById}
                weightUnit={weightUnit}
                aggregated={aggregated}
                organizationId={organizationId}
                clientId={clientId}
                onEditWorkout={setEditorWorkout}
              />
            </AccordionDetails>
          </Accordion>
        );
      })}
      <EditWorkoutDialog
        open={Boolean(editorWorkout)}
        workout={editorWorkout}
        organizationId={organizationId}
        clientId={clientId}
        onClose={() => setEditorWorkout(null)}
      />
    </Box>
  );
}

export default RecentWorkoutSessionsList;
